import { arrow, autoUpdate, computePosition, flip, offset, platform, shift, size } from '@floating-ui/dom';
import { TemplateElement, html, defineElement } from '@webtides/element-js';
import { randomUUID } from '@webtides/element-js/src/util/crypto';
import style from './tooltip.css';

/**
 * Tooltip component to make it declarative and accessible
 * @property {string} trigger
 * @property {string} placement
 * @property {string} content
 */
export default class Tooltip extends TemplateElement {
    uuid = randomUUID();

    constructor() {
        super({ styles: [style], shadowRender: true });
    }

    properties() {
        return {
            trigger: 'hover focus',
            placement: 'top',
            content: '',
        };
    }

    afterUpdate() {
        computePosition(this.$refs.trigger, this.$refs.tooltip, {
            placement: this.placement,
            middleware: [offset(16), flip(), shift(), arrow({ element: this.$refs.arrow })],
        }).then(({ x, y, placement, middlewareData }) => {
            Object.assign(this.$refs.tooltip.style, {
                left: `${x - 8}px`, // minus the width of the arrow
                top: `${y}px`,
            });

            const { x: arrowX, y: arrowY } = middlewareData.arrow;

            const staticSide = {
                top: 'bottom',
                right: 'left',
                bottom: 'top',
                left: 'right',
            }[placement.split('-')[0]];

            Object.assign(this.$refs.arrow.style, {
                left: arrowX != null ? `${arrowX}px` : '',
                top: arrowY != null ? `${arrowY}px` : '',
                right: '',
                bottom: '',
                [staticSide]: '-4px',
            });
        });
    }

    events() {
        return {
            this: {
                mouseover: () => {
                    if (this.trigger.includes('hover')) {
                        this.show();
                    }
                },
                mouseout: () => {
                    if (this.trigger.includes('hover')) {
                        setTimeout(() => {
                            const hoveredTooltip = this.getRoot().querySelector(`#tooltip:hover`);
                            if (!hoveredTooltip) {
                                this.hide();
                            }
                        }, 250);
                    }
                },
                focusin: () => {
                    if (this.trigger.includes('focus')) {
                        this.show();
                    }
                },
                focusout: () => {
                    if (this.trigger.includes('focus')) {
                        this.hide();
                    }
                },
                keydown: (e) => {
                    if (e.key === 'Escape') {
                        this.hide();
                    }
                },
            },
        };
    }

    show() {
        this.$refs.tooltip.style.display = 'block';
        this.requestUpdate();
    }

    hide() {
        this.$refs.tooltip.style.display = '';
        this.requestUpdate();
    }

    template() {
        return html`
            <div ref="trigger" id="trigger">
                <slot></slot>
            </div>
            <div ref="tooltip" id="tooltip" role="tooltip">
                ${this.content}
                <div ref="arrow" id="arrow"></div>
            </div>
        `;
    }
}

defineElement('mb-tooltip', Tooltip);
